import React, { memo, useCallback } from 'react';
import { IconClose, InformationalScreen, LoadingModal } from '@adc/parallax-component-library';
import { LocalDate } from '@js-joda/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BirthdayInput, Footer, FormContainer, Input, MainContainer } from 'Components/utility';
import { SHOW_MODAL } from 'Reducers/modal/types';
import i18n from 'Utilities/i18n';
import log from 'Utilities/log';
import { store } from 'Utilities/store';
const AccountInfoAdult = ({ patient, app, firstNameLimit, lastNameLimit, minAge, loading, onSave, onBack, hasFormChanged, validateDOBAndDisplayError, validateEntriesLengthAndDisplayError, validateIsMinor, showMinorRestrictionMessage, }) => {
    const validationSchema = Yup.object().shape({
        firstName: Yup.string()
            .required(i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoFormMinor.formField.firstName.errors.required'))
            .trim(),
        lastName: Yup.string()
            .required(i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoFormMinor.formField.lastName.errors.required'))
            .trim(),
        dateOfBirth: Yup.string().required(i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoFormMinor.formField.dob.errors.required')),
    });
    const onSubmitForm = useCallback(async (values) => {
        const isValid = validateEntriesLengthAndDisplayError([
            {
                value: values.firstName,
                msgKey: 'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.firstNameMaxLength',
                count: firstNameLimit,
            },
            {
                value: values.lastName,
                msgKey: 'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.lastNameMaxLength',
                count: lastNameLimit,
            },
        ]);
        if (!isValid) {
            return;
        }
        try {
            onSave(values);
        }
        catch (err) {
            log.error(err);
        }
    }, [firstNameLimit, lastNameLimit, onSave, validateEntriesLengthAndDisplayError]);
    const { handleSubmit, handleChange, handleBlur, setFieldValue, setFieldTouched, isValid, isSubmitting, errors, touched, values, initialValues, } = useFormik({
        initialValues: patient,
        validationSchema: validationSchema,
        onSubmit: onSubmitForm,
        validateOnBlur: true,
    });
    const handleOnBack = useCallback(() => {
        onBack(initialValues, values);
    }, [initialValues, values, onBack]);
    return (React.createElement(MainContainer, null,
        loading && React.createElement(LoadingModal, { "aria-label": "Loading" }),
        React.createElement(InformationalScreen, { topNavigationProps: {
                title: i18n.t('AccountInformationPersonalInfo.title'),
                buttonStart: {
                    element: React.createElement(IconClose, { testID: "AccountInformationPersonalInfo.go-back" }),
                    onPress: handleOnBack,
                },
                testID: 'AccountInformationPersonalInfo.title',
            }, testID: "AccountInformationPersonalInfo" },
            React.createElement(InformationalScreen.Page, null,
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(FormContainer, null,
                        React.createElement(Input, { "aria-label": i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.label'), placeholder: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.placeholder'), label: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.label'), testID: "firstName", errorMessage: errors.firstName, isInvalid: errors.firstName && touched.firstName ? true : false, value: values.firstName, onChangeText: handleChange('firstName'), onBlur: handleBlur('firstName') }),
                        React.createElement(Input, { "aria-label": i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.label'), placeholder: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.placeholder'), label: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.label'), testID: "lastName", inputMode: "text", errorMessage: errors.lastName, isInvalid: errors.lastName && touched.lastName ? true : false, value: values.lastName, onChangeText: handleChange('lastName'), onBlur: handleBlur('lastName') }),
                        React.createElement(BirthdayInput, { error: errors.dateOfBirth && touched.dateOfBirth ? true : false, errorMessage: errors.dateOfBirth, testID: "dateOfBirth", initialDate: values.dateOfBirth ? LocalDate.parse(values.dateOfBirth) : undefined, onDateChange: (date) => {
                                setFieldTouched('dateOfBirth');
                                const dateString = date.toString();
                                const isValidDate = validateDOBAndDisplayError(dateString);
                                if (isValidDate) {
                                    const isMinor = validateIsMinor(dateString, minAge);
                                    if (isMinor) {
                                        if (app === 'llu') {
                                            showMinorRestrictionMessage(minAge);
                                            return;
                                        }
                                        store.dispatch({
                                            type: SHOW_MODAL,
                                            modalProps: {
                                                details: i18n.t('AccountInformationPersonalInfo.modals.minorDOBError.description'),
                                            },
                                        });
                                        return;
                                    }
                                    setFieldValue('dateOfBirth', dateString);
                                    setFieldTouched('dateOfBirth');
                                }
                            } }),
                        React.createElement(Input, { "aria-label": i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.country.label'), label: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.country.label'), testID: "country", value: i18n.t(`Global.microcopy.countries.${values.country}`), disabled: true }),
                        React.createElement(Input, { "aria-label": i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.emailAddress.label'), label: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.emailAddress.label'), testID: "email", inputMode: "text", value: values.email, disabled: true }))),
                React.createElement(InformationalScreen.Footer, null,
                    React.createElement(Footer, { buttonLabel: i18n.t('AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.links.saveButton.label'), onButtonPress: handleSubmit, buttonTestID: "AccountInformationPersonalInfo.saveButton", disabled: !values.firstName ||
                            !values.lastName ||
                            !values.dateOfBirth ||
                            !isValid ||
                            isSubmitting ||
                            !hasFormChanged(initialValues, values) }))))));
};
export default memo(AccountInfoAdult);
